import React, {createContext, useCallback, useContext, useRef} from 'react';

export interface ValidationSection {
  id: string;
  label: string;
  validateForm: () => Promise<boolean>;
  reveal?: () => void;
}

export interface SectionValidationResult {
  id: string;
  label: string;
  valid: boolean;
}

export interface SectionValidationContextValue {
  registerSection: (section: ValidationSection) => () => void;
  validateAll: () => Promise<SectionValidationResult[]>;
}

const SectionValidationContext = createContext<
  SectionValidationContextValue | undefined
>(undefined);

export interface SectionValidationProviderProps {
  children: React.ReactNode;
}

export const SectionValidationProvider: React.FC<
  SectionValidationProviderProps
> = ({children}) => {
  const sectionsRef = useRef<Map<string, ValidationSection>>(new Map());

  const registerSection = useCallback((section: ValidationSection) => {
    sectionsRef.current.set(section.id, section);
    return () => {
      if (sectionsRef.current.get(section.id) === section) {
        sectionsRef.current.delete(section.id);
      }
    };
  }, []);

  // Reveals the first invalid section (registration order) as a side effect
  // of validating.
  const validateAll = useCallback(async (): Promise<SectionValidationResult[]> => {
    const sections = Array.from(sectionsRef.current.values());
    const results = await Promise.all(
      sections.map(async section => ({
        id: section.id,
        label: section.label,
        valid: await section.validateForm(),
      }))
    );

    const firstInvalidIndex = results.findIndex(result => !result.valid);
    if (firstInvalidIndex !== -1) {
      sections[firstInvalidIndex].reveal?.();
    }

    return results;
  }, []);

  const value = React.useMemo<SectionValidationContextValue>(
    () => ({registerSection, validateAll}),
    [registerSection, validateAll]
  );

  return (
    <SectionValidationContext.Provider value={value}>
      {children}
    </SectionValidationContext.Provider>
  );
};

export const useSectionValidation = (): SectionValidationContextValue => {
  const context = useContext(SectionValidationContext);
  if (!context) {
    throw new Error(
      'useSectionValidation must be used within a SectionValidationProvider'
    );
  }
  return context;
};
